import graphql from "babel-plugin-relay/macro";
import { AccountDetailsForm } from "components/account/AccountDetailsForm";
import { AccountWizardPage } from "components/account/AccountWizardPage";
import { ConfirmForm } from "components/account/ConfirmForm";
import { AddressForm } from "components/address/AddressForm";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { LoadingRegion } from "components/shared/LoadingRegion";
import { LoadingRegionErrorBoundary } from "components/shared/LoadingRegionErrorBoundary";
import { LoadingRegionSuspense } from "components/shared/LoadingRegionSuspense";
import { accountMessages } from "domain/account/accountMessages";
import { EditAccountQuery } from "generatedQueries/EditAccountQuery.graphql";
import { LocationState } from "history";
import { FC, useRef } from "react";
import { useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import { routes } from "routes/routes";
import { BooleanMessage } from "shared/components/BooleanMessage";
import { HideableFields, IsHiddenField } from "shared/components/Hideable";
import { Query } from "shared/components/Query";
import { Wizard } from "shared/components/Wizard";
import { WizardPage } from "shared/components/WizardPage";
import { mapMessages } from "shared/mapMessages";
import { sharedMessages } from "shared/sharedMessages";

const messages = mapMessages("routes.account.EditAccount", {
    contentName: "Account Wizard",
    formName: "Edit account",
    stepContentLabel: "step {stepNumber} out of {stepCount}: {contentName}",
});

export interface IEditAccountLocationState<T = LocationState> {
    nextPagePath: string;
    nextPageState?: T;
}

export const EditAccount: FC = () => {
    const { formatMessage } = useIntl();
    const history = useHistory();
    const location = useLocation<IEditAccountLocationState | LocationState>();
    const isNewAccountRef = useRef<boolean | null>(null);

    return <Query<EditAccountQuery>
        query={graphql`
            query EditAccountQuery {
                account {
                    status
                }
                configuration {
                    hiddenFields
                }
            }`}
        variables={{}}>
        {({ error, props: renderProps }) => (
            <main>
                <LoadingRegion contentName={messages.contentName} hasError={!!error} isLoading={!renderProps}>
                {(() => {
                    if (error) {
                        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                    }

                    if (!renderProps) {
                        return;
                    }

                    const { account, configuration } = renderProps;

                    if (!account) {
                        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                    }

                    if (isNewAccountRef.current === null) {
                        isNewAccountRef.current = account.status === "NEW";
                    }
                    const isNewAccount = isNewAccountRef.current;

                    const showConfirmForm = configuration &&
                        !(IsHiddenField(HideableFields.canDiscloseToPublic, configuration.hiddenFields) &&
                            IsHiddenField(HideableFields.isSubscribedToShelterNewsletter, configuration.hiddenFields));
                    const stepCount = showConfirmForm ? 4 : 3;

                    return <>
                        <h1 className="h2">
                            {
                                <BooleanMessage
                                    value={isNewAccount}
                                    confirmTrueMessage={accountMessages.accountNewPageTitle}
                                    confirmFalseMessage={accountMessages.accountExistingPageTitle} />
                            }
                        </h1>
                        <hr />
                        <Wizard
                            onComplete={onComplete(isNewAccount)}
                            formName={formatMessage(messages.formName)}
                        >
                            <WizardPage
                                title={accountMessages.accountDetailsStepTitle}
                                description={accountMessages.accountDetailsStepDescriptionLabel}
                                completeIcon="check-circle">
                                {({ onNext }) => {
                                    const contentLabel = formatMessage(messages.stepContentLabel, { stepNumber: 1, stepCount, contentName: formatMessage(accountMessages.accountDetailsStepDescriptionLabel) });
                                    return <section aria-label={contentLabel}>
                                        <LoadingRegionErrorBoundary contentName={contentLabel}>
                                            <LoadingRegionSuspense contentName={contentLabel}>
                                                <AccountDetailsForm onNext={onNext} isNewAccount={isNewAccount} pageContentName={contentLabel} />
                                            </LoadingRegionSuspense>
                                        </LoadingRegionErrorBoundary>
                                    </section>;
                                }}
                            </WizardPage>
                            <WizardPage
                                title={accountMessages.addressStepTitle}
                                description={accountMessages.addressStepDescriptionLabel}
                                completeIcon="check-circle">
                                {({ onNext, onPrevious }) => {
                                    const contentLabel = formatMessage(messages.stepContentLabel, { stepNumber: 2, stepCount, contentName: formatMessage(accountMessages.addressStepDescriptionLabel) });
                                    return <section aria-label={contentLabel}>
                                        <LoadingRegionErrorBoundary contentName={contentLabel}>
                                            <LoadingRegionSuspense contentName={contentLabel}>
                                                <AddressForm
                                                    onNext={onNext}
                                                    onBack={onPrevious}
                                                    isNewAccount={isNewAccount}
                                                    pageContentName={contentLabel} />
                                            </LoadingRegionSuspense>
                                        </LoadingRegionErrorBoundary>
                                    </section>;
                                }}
                            </WizardPage>
                            {
                                showConfirmForm
                                &&
                                <WizardPage
                                    title={accountMessages.stepThreeTitle}
                                    description={accountMessages.additionalDetailsLabel}
                                    completeIcon="check-circle">
                                    {({ onNext, onPrevious }) => {
                                        const contentLabel = formatMessage(messages.stepContentLabel, { stepNumber: 3, stepCount, contentName: formatMessage(accountMessages.additionalDetailsLabel) });
                                        return <section aria-label={contentLabel}>
                                            <LoadingRegionErrorBoundary contentName={contentLabel}>
                                                <LoadingRegionSuspense contentName={contentLabel}>
                                                    <ConfirmForm
                                                        onNext={onNext}
                                                        onBack={onPrevious}
                                                        nextButtonMessage={sharedMessages.nextButtonLabel}
                                                        pageContentName={contentLabel} />
                                                </LoadingRegionSuspense>
                                            </LoadingRegionErrorBoundary>
                                        </section>;
                                    }}
                                </WizardPage>
                            }
                            <WizardPage
                                title={showConfirmForm ? accountMessages.stepFourTitle : accountMessages.stepThreeTitle}
                                description={accountMessages.confirmAccountStepDescription}
                                completeIcon="check-circle">
                                {({ onPrevious, onNext }) =>
                                    <section aria-label={formatMessage(messages.stepContentLabel, { stepNumber: stepCount, stepCount, contentName: formatMessage(accountMessages.confirmAccountStepDescription) })}>
                                        <AccountWizardPage
                                            isNewAccount={isNewAccount}
                                            hasAdditionalDetailsForm={showConfirmForm ?? false}
                                            onPrevious={onPrevious}
                                            onNext={onNext} />
                                    </section>
                                }
                            </WizardPage>
                        </Wizard>
                    </>;
                })()}
            </LoadingRegion>
        </main>
        )}
    </Query>;

    function onComplete(isNewAccount: boolean) {
        return () => {
            if (isEditLocationState(location.state)) {
                history.push(location.state.nextPagePath, location.state.nextPageState);
                return;
            }

            history.push(isNewAccount ? routes.licence.options.create({}) : routes.home.create({}));
        };
    }
};

function isEditLocationState(state: LocationState): state is IEditAccountLocationState {
    if (!state) {
        return false;
    }

    return (state as IEditAccountLocationState).nextPagePath !== undefined;
}
